import { createContext, useContext } from 'react';
import { supabase } from './supabase';
import { logger } from './logger';
import type { Session } from '@supabase/supabase-js';

export interface AdminUser {
  id: string;
  email: string;
  role: string;
}

interface AuthState {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  loading: true,
  signIn: async () => null,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export async function verifyAdminRole(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();
    if (error) {
      logger.error('verify_admin_role', 'auth', { userId, error: error.message });
      return false;
    }
    const isAdmin = data?.role === 'admin';
    logger.technical('verify_admin_role_ok', 'auth', { userId, isAdmin });
    return isAdmin;
  } catch (err) {
    logger.error('verify_admin_role', 'auth', { userId, error: String(err) });
    return false;
  }
}

export async function signInAdmin(email: string, password: string): Promise<{ user: AdminUser } | { error: string }> {
  logger.functional('sign_in_attempt', 'auth', { email });
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    logger.error('sign_in_failed', 'auth', { email, error: error.message });
    return { error: error.message };
  }
  if (!data.user) {
    logger.error('sign_in_no_user', 'auth', { email });
    return { error: 'Connexion échouée.' };
  }

  const isAdmin = await verifyAdminRole(data.user.id);
  if (!isAdmin) {
    logger.warn('sign_in_not_admin', 'auth', { email, userId: data.user.id });
    await supabase.auth.signOut();
    return { error: 'Accès réservé aux administrateurs.' };
  }

  logger.functional('sign_in_success', 'auth', { email, userId: data.user.id });
  return {
    user: {
      id: data.user.id,
      email: data.user.email || email,
      role: 'admin',
    },
  };
}
